<div class="d-flex flex-wrap justify-content-between align-items-start mb-3">
  <div>
    <h5 class="mb-1 fw-bold">Production Entries</h5>
    <span class="sub-text">History of manufactured products</span>
  </div>
  <div class="d-flex gap-2">
    <button class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2" (click)="openExportModal()">
      <i class="bi bi-download"></i>
      Export Report
    </button>
    <button class="btn btn-primary btn-enjochem" (click)="openProductionEntryFormModal(productionEntryFormContent)">
      <i class="bi bi-plus-circle"></i>
      Add Entry
    </button>
  </div>
</div>

<div class="alert alert-info d-flex align-items-start mb-3" role="alert">
  <i class="bi bi-info-circle-fill me-2 mt-1"></i>
  <div>
    <strong>Production entries cannot be edited.</strong>
    Deleting one is the only correction: the units produced come back out of factory stock, and the raw material
    consumed goes back to the warehouse it came from. A batch that has already been transferred out of the factory, or
    that still holds warehouse stock, has to be cleared before it will delete.
  </div>
</div>

<!-- Filters -->
<div class="row g-2 mb-4 align-items-center">
  <!-- From Date -->
  <div class="col-12 col-md-3">
    <div class="input-group">
      <span class="input-group-text bg-white text-muted">From</span>
      <input type="date" class="form-control form-control-sm" [(ngModel)]="fromDate" (change)="onFilterChange()" />
    </div>
  </div>

  <!-- To Date -->
  <div class="col-12 col-md-3">
    <div class="input-group">
      <span class="input-group-text bg-white text-muted">To</span>
      <input type="date" class="form-control form-control-sm" [(ngModel)]="toDate" (change)="onFilterChange()" />
    </div>
  </div>

  <!-- Product -->
  <div class="col-12 col-md-3">
    <ng-select
      [items]="productNameList"
      bindLabel="name"
      bindValue="id"
      placeholder="All Products"
      [(ngModel)]="selectedProduct"
      (change)="onFilterChange()"
      [clearable]="true"
      class="ng-select-sm"></ng-select>
  </div>

  <!-- Factory -->
  <div class="col-12 col-md-3">
    <ng-select
      [items]="factoryList"
      bindLabel="name"
      bindValue="id"
      placeholder="All Factories"
      [(ngModel)]="selectedFactory"
      (change)="onFilterChange()"
      [clearable]="true"
      class="ng-select-sm"></ng-select>
  </div>
</div>

@if (deleteError) {
  <app-error-alert [error]="deleteError" [showFieldNames]="false" />
}

@if (blockedByTransfers) {
  <div class="mb-3">
    <button
      type="button"
      class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
      (click)="openBlockingTransfers()">
      <i class="bi bi-arrow-right-circle"></i>
      Open the {{ blockedByTransfers.transferIds.length }} blocking transfer(s)
    </button>
  </div>
}

@if (isDataLoading || isDeletingEntry) {
  <app-loading-spinner></app-loading-spinner>
} @else {
  @if (productionEntries && productionEntries.length === 0) {
    <div class="text-center py-4 text-muted">
      <app-info-alert>
        No production entries found.
        <br />
        Start by adding a new
        <strong>Production Entry</strong>
        .
      </app-info-alert>
    </div>
  } @else {
    <div class="table-responsive">
      <app-table
        [columns]="columns"
        [data]="productionEntries"
        [actions]="actions"
        (onDelete)="onDeleteEntry($event)"
        (onClickAddEntry)="openViewModal(viewProductionModal, $event)"></app-table>
    </div>
  }

  @if (productionEntries && productionEntries.length) {
    <div class="mt-4">
      <app-pagination
        [currentPage]="currentPage"
        [totalCount]="totalCount"
        [pageSize]="10"
        [hasNextPage]="hasNextPage"
        [hasPreviousPage]="hasPreviousPage"
        (pageChange)="onPageChange($event)"></app-pagination>
    </div>
  }
}

<!-- Export Modal -->
<ng-template #exportModal let-modal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold">Export Production Entries</h5>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body">
    @if (exportError) {
      <app-error-alert [error]="exportError" class="mb-3"></app-error-alert>
    }

    <div class="row g-3">
      <!-- Date Range -->
      <div class="col-6">
        <label class="form-label">From Date</label>
        <input type="date" class="form-control" [(ngModel)]="exportFromDate" />
      </div>
      <div class="col-6">
        <label class="form-label">To Date</label>
        <input type="date" class="form-control" [(ngModel)]="exportToDate" />
      </div>

      <!-- Factory -->
      <div class="col-12">
        <label class="form-label">Factory</label>
        <ng-select
          [items]="factoryList"
          bindLabel="name"
          bindValue="id"
          placeholder="All Factories"
          [(ngModel)]="exportFactory"
          [clearable]="true"></ng-select>
      </div>

      <!-- Product -->
      <div class="col-12">
        <label class="form-label">Product</label>
        <ng-select
          [items]="productNameList"
          bindLabel="name"
          bindValue="id"
          placeholder="All Products"
          [(ngModel)]="exportProduct"
          [clearable]="true"
          [searchable]="true"></ng-select>
      </div>

      <!-- Company -->
      <div class="col-12">
        <label class="form-label">Company</label>
        <ng-select
          [items]="companyList"
          bindLabel="name"
          bindValue="id"
          placeholder="All Companies"
          [(ngModel)]="exportCompany"
          [clearable]="true"></ng-select>
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-secondary btn-sm" (click)="modal.dismiss()" [disabled]="isExporting">
      Cancel
    </button>
    <button
      type="button"
      class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
      (click)="onExportSubmit(modal)"
      [disabled]="isExporting">
      @if (isExporting) {
        <span class="spinner-border spinner-border-sm"></span>
        Exporting...
      } @else {
        <i class="bi bi-download"></i>
        Export
      }
    </button>
  </div>
</ng-template>

<ng-template #productionEntryFormContent>
  <div class="modal-content border-0 shadow-lg">
    <div class="modal-header">
      <h5 class="modal-title">Add Production Entry</h5>
      <button type="button" class="btn-close" (click)="modalRef?.dismiss()"></button>
    </div>

    <div class="modal-body">
      <app-error-alert [error]="formError" />

      <app-production-entry-form
        [factoryList]="factoryList"
        [companyList]="companyList"
        (productionEntryEvent)="onSaveProductionEntry($event)" />
    </div>
  </div>
</ng-template>

<ng-template #viewProductionModal let-modal>
  <div class="modal-content border-0 shadow-lg">
    <div class="modal-header border-bottom-0 pb-0">
      <h5 class="modal-title fw-bold">Production Details</h5>
      <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
    </div>

    <div class="modal-body px-4 pb-4 pt-3">
      @if (selectedEntry) {
        <!-- Production meta -->
        <div class="row g-0 mb-4">
          <div class="col-6 pe-3">
            <div class="extra-small text-muted fw-semibold text-uppercase mb-1">Production No</div>
            <div class="fw-medium">{{ selectedEntry.production_number || '—' }}</div>
          </div>
          <div class="col-6 ps-3 border-start">
            <div class="extra-small text-muted fw-semibold text-uppercase mb-1">Batch No</div>
            <div class="fw-medium">{{ selectedEntry.batch_number || '—' }}</div>
          </div>
          <div class="col-12"><hr class="my-3" /></div>
          <div class="col-6 pe-3">
            <div class="extra-small text-muted fw-semibold text-uppercase mb-1">Product</div>
            <div class="fw-medium text-primary">{{ selectedEntry.product_name || '—' }}</div>
          </div>
          <div class="col-6 ps-3 border-start">
            <div class="extra-small text-muted fw-semibold text-uppercase mb-1">Factory</div>
            <div class="fw-medium">{{ selectedEntry.factory_name || '—' }}</div>
          </div>
          <div class="col-12"><hr class="my-3" /></div>
          <div class="col-6 pe-3">
            <div class="extra-small text-muted fw-semibold text-uppercase mb-1">Production Date</div>
            <div class="fw-medium">
              {{ selectedEntry.production_date ? (selectedEntry.production_date | date: 'mediumDate') : '—' }}
            </div>
          </div>
          <div class="col-6 ps-3 border-start">
            <div class="extra-small text-muted fw-semibold text-uppercase mb-1">Mfg / Expiry</div>
            <div class="fw-medium small">
              @if (selectedEntry.manufacturing_date) {
                Mfg: {{ selectedEntry.manufacturing_date | date: 'mediumDate' }}
              }
              @if (selectedEntry.expiry_date) {
                &nbsp;· Exp: {{ selectedEntry.expiry_date | date: 'mediumDate' }}
              }
              @if (!selectedEntry.manufacturing_date && !selectedEntry.expiry_date) {
                —
              }
            </div>
          </div>
        </div>

        @if (deleteError) {
          <app-error-alert [error]="deleteError" [showFieldNames]="false" />
        }

        @if (blockedByTransfers) {
          <div class="mb-3">
            <button
              type="button"
              class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
              (click)="modal.dismiss(); openBlockingTransfers()">
              <i class="bi bi-arrow-right-circle"></i>
              Open the {{ blockedByTransfers.transferIds.length }} blocking transfer(s)
            </button>
          </div>
        }

        <!-- Shared error for all consumption actions -->
        @if (consumptionError) {
          @if (isInsufficientStockError(consumptionError)) {
            <div class="alert alert-danger py-2 px-3 small mb-3">
              <div class="fw-semibold mb-1">Insufficient stock — {{ consumptionError.raw_material }}</div>
              <div class="d-flex gap-3">
                <span>
                  Available:
                  <strong>{{ consumptionError.available }}</strong>
                </span>
                <span>
                  Required:
                  <strong>{{ consumptionError.required }}</strong>
                </span>
                <span class="text-danger">
                  Shortage:
                  <strong>{{ consumptionError.shortage }}</strong>
                </span>
              </div>
            </div>
          } @else {
            <app-error-alert [error]="consumptionError" />
          }
        }

        <!-- Section heading -->
        <div class="d-flex align-items-center justify-content-between mb-2">
          <span class="extra-small fw-semibold text-uppercase text-muted" style="letter-spacing: 0.05em">
            Raw Material Consumption
          </span>
          @if (selectedEntry.raw_material_consumptions.length > 0) {
            <span class="badge bg-secondary-subtle text-secondary rounded-pill small">
              {{ selectedEntry.raw_material_consumptions.length }}
            </span>
          }
        </div>

        <!-- Logged consumptions list -->
        @if (selectedEntry.raw_material_consumptions.length > 0) {
          <div class="border rounded overflow-hidden mb-3">
            <table class="table table-sm mb-0">
              <thead class="bg-light">
                <tr>
                  <th class="ps-3 py-2 small fw-semibold text-muted border-0">Raw Material</th>
                  <th class="py-2 small fw-semibold text-muted border-0">Warehouse</th>
                  <th class="py-2 small fw-semibold text-muted border-0 text-end">Quantity</th>
                  <th class="py-2 border-0" style="width: 44px"></th>
                </tr>
              </thead>
              <tbody>
                @for (mat of selectedEntry.raw_material_consumptions; track mat.id) {
                  <tr>
                    <td class="ps-3 py-2 align-middle">
                      <div class="fw-medium small">{{ mat.raw_material_name }}</div>
                      <div class="extra-small text-muted">{{ mat.raw_material_code }}</div>
                    </td>
                    <td class="py-2 align-middle">
                      <div class="small text-muted">{{ mat.warehouse_name || '—' }}</div>
                    </td>
                    <td class="py-2 pe-3 text-end align-middle">
                      <span class="fw-medium small">{{ mat.quantity_consumed }}</span>
                      <span class="extra-small text-muted ms-1">{{ mat.unit_name }}</span>
                    </td>
                    <td class="py-2 pe-2 text-end align-middle">
                      <button
                        class="btn btn-sm btn-link text-danger p-0"
                        style="line-height: 1"
                        (click)="onDeleteConsumption(mat.id)"
                        [disabled]="deletingConsumptionId === mat.id">
                        @if (deletingConsumptionId === mat.id) {
                          <span class="spinner-border spinner-border-sm"></span>
                        } @else {
                          <i class="bi bi-trash"></i>
                        }
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <p class="text-muted small mb-3">No consumptions logged yet.</p>
        }

        <!-- BOM checklist -->
        @if (isBomLoading) {
          <div class="text-muted small mb-3">
            <span class="spinner-border spinner-border-sm me-1"></span>
            Loading BOM...
          </div>
        } @else if (bomResponse?.has_bom && bomResponse!.materials.length > 0) {
          <div class="border rounded overflow-hidden mb-3">
            <div class="px-3 py-2 bg-light border-bottom">
              <div class="d-flex align-items-center gap-3">
                <span class="extra-small fw-semibold text-uppercase text-muted" style="letter-spacing: 0.05em">
                  Bill of Materials
                </span>
                <div class="d-flex align-items-center gap-2 ms-auto">
                  <label class="form-label small mb-0 text-nowrap text-muted">Warehouse</label>
                  <select
                    class="form-select form-select-sm"
                    [(ngModel)]="bomWarehouseId"
                    [disabled]="isBomWarehouseLocked"
                    style="min-width: 180px">
                    <option [ngValue]="null">Select warehouse...</option>
                    @for (w of warehouses; track w.id) {
                      <option [value]="w.id">{{ w.name }}</option>
                    }
                  </select>
                  @if (isBomWarehouseLocked) {
                    <i class="bi bi-lock-fill text-muted small"></i>
                  }
                </div>
              </div>
            </div>
            <table class="table table-sm mb-0 align-middle">
              <thead class="bg-light border-bottom">
                <tr>
                  <th class="ps-3 py-2 small fw-semibold text-muted border-0">Material</th>
                  <th class="py-2 small fw-semibold text-muted border-0 text-end" style="width: 90px">Required</th>
                  <th class="py-2 small fw-semibold text-muted border-0" style="width: 105px">Actual Qty</th>
                  <th class="py-2 border-0" style="width: 64px"></th>
                </tr>
              </thead>
              <tbody>
                @for (mat of bomResponse!.materials; track mat.raw_material) {
                  @if (isBomMaterialLogged(mat.raw_material)) {
                    <tr class="table-success">
                      <td class="ps-3 py-2">
                        <div class="fw-medium small">{{ mat.raw_material_name }}</div>
                        <div class="extra-small text-muted">{{ mat.raw_material_code }}</div>
                      </td>
                      <td class="py-2 text-end small text-muted">{{ mat.quantity_required }} {{ mat.unit }}</td>
                      <td class="py-2 small text-muted">logged</td>
                      <td class="py-2 pe-3 text-end">
                        <i class="bi bi-check-circle-fill text-success"></i>
                      </td>
                    </tr>
                  } @else {
                    <tr>
                      <td class="ps-3 py-2">
                        <div class="fw-medium small">{{ mat.raw_material_name }}</div>
                        <div class="extra-small text-muted">{{ mat.raw_material_code }}</div>
                      </td>
                      <td class="py-2 text-end">
                        <span class="fw-medium small">{{ mat.quantity_required }}</span>
                        <span class="extra-small text-muted ms-1">{{ mat.unit }}</span>
                      </td>
                      <td class="py-2">
                        <input
                          type="number"
                          class="form-control form-control-sm"
                          step="0.01"
                          min="0.001"
                          [(ngModel)]="getBomRowState(mat.raw_material).qty"
                          [disabled]="getBomRowState(mat.raw_material).isLogging" />
                      </td>
                      <td class="py-2 pe-3 text-end">
                        <button
                          type="button"
                          class="btn btn-sm btn-primary"
                          [disabled]="
                            !bomWarehouseId ||
                            !getBomRowState(mat.raw_material).qty ||
                            getBomRowState(mat.raw_material).isLogging
                          "
                          (click)="onLogBomConsumption(mat)">
                          @if (getBomRowState(mat.raw_material).isLogging) {
                            <span class="spinner-border spinner-border-sm"></span>
                          } @else {
                            Log
                          }
                        </button>
                      </td>
                    </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        }

        <!-- Manual consumption form -->
        <!-- No overflow-hidden here: it clips the raw material dropdown, which opens -->
        <!-- outside this box. The header rounds its own top corners instead. -->
        <div class="border rounded">
          <div class="px-3 py-2 bg-light border-bottom rounded-top">
            <div class="d-flex align-items-center gap-3">
              <span class="extra-small fw-semibold text-uppercase text-muted" style="letter-spacing: 0.05em">
                {{ bomResponse?.has_bom ? 'Add Extra Material' : 'Log Consumption' }}
              </span>
              <div class="d-flex align-items-center gap-2 ms-auto">
                <label class="form-label small mb-0 text-nowrap text-muted">Warehouse</label>
                <select class="form-select form-select-sm" [(ngModel)]="extraWarehouseId" style="min-width: 180px">
                  <option [ngValue]="null">Select warehouse...</option>
                  @for (w of warehouses; track w.id) {
                    <option [value]="w.id">{{ w.name }}</option>
                  }
                </select>
              </div>
            </div>
          </div>
          <div class="p-3">
            <form [formGroup]="consumptionForm" (ngSubmit)="onAddConsumption()">
              <div class="row g-2 align-items-end">
                <div class="col">
                  <label class="form-label small mb-1">Raw Material</label>
                  <ng-select
                    formControlName="raw_material"
                    placeholder="Search raw material..."
                    [items]="rawMaterialList"
                    bindLabel="name">
                    <ng-template ng-label-tmp let-item="item">
                      <span class="text-muted font-monospace me-1">{{ item.category_short_code }}</span>
                      &ndash; {{ item.name }}
                    </ng-template>
                    <ng-template ng-option-tmp let-item="item">
                      <span class="text-muted font-monospace me-1">{{ item.category_short_code }}</span>
                      &ndash; {{ item.name }}
                    </ng-template>
                  </ng-select>
                </div>
                <div class="col-auto" style="width: 120px">
                  <label class="form-label small mb-1">Quantity</label>
                  <input
                    type="number"
                    class="form-control"
                    formControlName="quantity_consumed"
                    step="0.01"
                    min="0.001"
                    placeholder="0.00" />
                </div>
                <div class="col-auto pb-1">
                  <button
                    class="btn btn-primary d-flex align-items-center gap-1"
                    [disabled]="consumptionForm.invalid || !extraWarehouseId || isAddingConsumption"
                    type="submit">
                    @if (isAddingConsumption) {
                      <span class="spinner-border spinner-border-sm"></span>
                    } @else {
                      <i class="bi bi-plus-lg"></i>
                    }
                    Add
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      }
    </div>

    @if (selectedEntry; as entry) {
      <div class="modal-footer justify-content-start gap-2">
        <button
          type="button"
          class="btn btn-outline-danger btn-sm d-flex align-items-center gap-2"
          (click)="onDeleteEntry(entry)"
          [disabled]="isDeletingEntry">
          @if (isDeletingEntry) {
            <span class="spinner-border spinner-border-sm"></span>
            Deleting...
          } @else {
            <i class="bi bi-trash"></i>
            Delete Entry
          }
        </button>
        <button
          type="button"
          class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
          (click)="openAdjustModal(entry, 'factory', adjustModal)">
          <i class="bi bi-sliders"></i>
          Adjust Factory Stock
        </button>
        <button
          type="button"
          class="btn btn-soft-primary btn-sm d-flex align-items-center gap-2"
          (click)="openAdjustModal(entry, 'warehouse', adjustModal)">
          <i class="bi bi-sliders"></i>
          Adjust Warehouse Stock
        </button>
        <div class="btn-group btn-group-sm">
          <button
            type="button"
            class="btn btn-soft-primary d-flex align-items-center gap-2"
            (click)="openHistoryModal(entry, 'factory', historyModal)">
            <i class="bi bi-clock-history"></i>
            Factory Adjustments
          </button>
          <button
            type="button"
            class="btn btn-soft-primary d-flex align-items-center gap-2"
            (click)="openHistoryModal(entry, 'warehouse', historyModal)">
            <i class="bi bi-clock-history"></i>
            Warehouse Adjustments
          </button>
        </div>
        <span class="extra-small text-muted">
          Delete reverses this batch's factory and warehouse stock. Adjust corrects one balance without touching the
          entry, and works even when the batch sits at zero and drops off the stock screens.
        </span>
      </div>
    }
  </div>
</ng-template>

<!-- Adjust factory stock for this batch -->
<ng-template #adjustModal>
  <div class="modal-header">
    <h5 class="modal-title fw-bold">Adjust Factory Stock</h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="onAdjustCancelled()"></button>
  </div>

  @if (adjustContext) {
    <app-product-stock-adjustment-form
      [context]="adjustContext"
      (saved)="onAdjustmentSaved()"
      (cancelled)="onAdjustCancelled()"></app-product-stock-adjustment-form>
  }
</ng-template>

<!-- Adjustment history for this batch -->
<ng-template #historyModal let-modal>
  <div class="modal-header">
    <div>
      <h5 class="modal-title fw-bold">Adjustment History</h5>
      @if (historyEntry) {
        <span class="text-muted small">
          Batch {{ historyEntry.batch_number }} &middot; {{ historyEntry.product_name }} &middot;
          {{ historyEntry.factory_name }}
        </span>
      }
    </div>
    <button type="button" class="btn-close" aria-label="Close" (click)="modal.dismiss()"></button>
  </div>

  <div class="modal-body p-4">
    @if (historyEntry) {
      <app-product-stock-adjustments
        [scope]="historyScope"
        [productionEntry]="historyEntry.id"
        [refreshToken]="historyRefreshToken"></app-product-stock-adjustments>
    }
  </div>
</ng-template>
